import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Scan() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | running | error | done
  const [error, setError] = useState("");
  const [decoded, setDecoded] = useState("");
  const scannerRef = useRef(null);
  const elemId = "qr-scanner-region";

  const start = async () => {
    setStatus("running");
    setError("");
    setDecoded("");
    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(elemId);
      }
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        throw new Error("No camera detected");
      }
      const cameraId =
        cameras.find((c) => /back|rear|environment/i.test(c.label))?.id ||
        cameras[cameras.length - 1].id;
      await scannerRef.current.start(
        cameraId,
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (text) => {
          handleDecoded(text);
        },
        () => {}
      );
    } catch (e) {
      setStatus("error");
      setError(e?.message || String(e));
    }
  };

  const stop = async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
    } catch (err) {
      console.warn("QR scanner stop failed", err);
    }
  };

  const handleDecoded = async (text) => {
    setDecoded(text);
    setStatus("done");
    await stop();
    // If QR contains a URL on the same origin → navigate. Otherwise show payload.
    try {
      const url = new URL(text);
      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search);
        return;
      }
    } catch (err) {
      console.info("QR payload is not a URL — showing raw value", err.message);
    }
  };

  useEffect(() => {
    start();
    return () => {
      stop();
    };
  }, []);

  return (
    <Layout title="Scan QR" breadcrumbs={["Mobile", "Scan"]}>
      <div className="max-w-md mx-auto">
        <div className="engineered-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="h-5 w-5 text-orange-500" />
            <div className="font-display font-bold text-lg">Point camera at asset label</div>
          </div>
          <div
            id={elemId}
            data-testid="qr-scanner-region"
            className="bg-zinc-950 aspect-square rounded-sm overflow-hidden"
          />
          {status === "running" && (
            <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1">
              <div className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
              Looking for QR code…
            </div>
          )}
          {status === "error" && (
            <div className="mt-3 p-3 border border-red-300 bg-red-50 rounded-sm text-sm text-red-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                <b>Camera unavailable.</b> {error}
                <div className="text-xs text-red-700 mt-1">
                  Grant camera permission to your browser and reload.
                </div>
              </div>
            </div>
          )}
          {status === "done" && decoded && (
            <div className="mt-3 p-3 border border-emerald-300 bg-emerald-50 rounded-sm text-sm">
              <div className="font-bold text-emerald-800 mb-1">Decoded</div>
              <div className="font-mono text-xs break-all" data-testid="qr-decoded">
                {decoded}
              </div>
            </div>
          )}
          <Button
            data-testid="qr-restart-btn"
            onClick={start}
            className="mt-3 w-full rounded-sm bg-zinc-950 hover:bg-zinc-800"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Restart Scanner
          </Button>
        </div>
      </div>
    </Layout>
  );
}
